/**
 * Wires all Postgres domain fetch instances and orchestration helpers.
 * @module dataAccumulator/useSingleOrderDomainFetches
 */
import { useCallback, useRef } from "react";
import { CandidService } from "../../api/services/candid/candidService";
import { WritebackHistoryResult } from "../../api/services/candid/candidWritebackHistoryTypes";
import {
  DenialQueryBucketFetchPayload,
  DenialQueryBucketService,
} from "../../api/services/denialQueryBucketService";
import {
  DenialQueryBucketSummaryResponse,
  DENIAL_QUERY_BUCKET_LIMIT,
} from "../../data-model/denialQueryBucket";
import { useAbortableFetch } from "../../hooks/useAbortableFetch";
import { getLocalDenialQueryBucketTimeContext } from "../../utils/formatDate";
import {
  SINGLE_ORDER_DOMAIN_IDS,
  SINGLE_ORDER_DOMAIN_ORCHESTRATION,
} from "./singleOrderDomainConfig";
import { SINGLE_ORDER_DOMAIN_FETCHERS } from "./singleOrderDomainFetchers";
import { SingleOrderDomainInstances } from "./types";

/**
 * Wires one {@link useAbortableFetch} instance per Postgres domain and exposes
 * orchestration helpers (`fetchAll`, `abortAll`, combined `loading`).
 *
 * All domain hooks are called unconditionally to satisfy the Rules of Hooks.
 * Every fetch function passed to `useAbortableFetch` is referentially stable
 * (module-level for the Postgres domains, `useCallback` for the rest), which
 * keeps `runFetch`, `fetchAll`, and `abortAll` stable across renders so mount
 * effects that depend on them run once instead of re-fetching in a loop.
 *
 * @returns Domain instances plus fetch orchestration state and helpers
 */
export const useSingleOrderDomainFetches = () => {
  const generalOrderDetails = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.generalOrderDetails,
  );
  const authOnFileDetails = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.authOnFileDetails,
  );
  const clinicalData = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.clinicalData,
  );
  const commentsData = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.commentsData,
  );
  const coverageDetails = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.coverageDetails,
  );
  const workflowConfig = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.workflowConfig,
  );
  const reviewsData = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.reviewsData,
  );
  const clinicalConditions = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.clinicalConditions,
  );
  const authorizationData = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.authorizationData,
  );
  const submissionData = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.submissionData,
  );
  const siblingOrders = useAbortableFetch(
    SINGLE_ORDER_DOMAIN_FETCHERS.siblingOrders,
  );
  const fetchDenialQueryBucketFn = useCallback(
    (signal: AbortSignal, payload: DenialQueryBucketFetchPayload) =>
      DenialQueryBucketService.getDenialQueryBucket(payload, signal),
    [],
  );
  const fetchCandidHistoryWritebackFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      CandidService.getWritebackHistory(
        {
          order_id: orderId,
          org_id: orgId,
        },
        signal,
      ),
    [],
  );

  const denialQueryBucket = useAbortableFetch<DenialQueryBucketSummaryResponse>(
    fetchDenialQueryBucketFn,
    { clearDataOnError: true },
  );
  const candidHistoryWriteback = useAbortableFetch<WritebackHistoryResult>(
    fetchCandidHistoryWritebackFn,
  );

  const domains: SingleOrderDomainInstances = {
    generalOrderDetails,
    authOnFileDetails,
    clinicalData,
    commentsData,
    coverageDetails,
    workflowConfig,
    reviewsData,
    clinicalConditions,
    authorizationData,
    submissionData,
    siblingOrders,
  };

  const fetchInFlightRef = useRef<string | null>(null);
  const loadedKeyRef = useRef<string | null>(null);
  // A pre-reset batch must not mark a later visit cached or release its in-flight guard.
  const fetchGenerationRef = useRef(0);

  const resetAll = useCallback(() => {
    fetchGenerationRef.current += 1;
    fetchInFlightRef.current = null;
    loadedKeyRef.current = null;
    SINGLE_ORDER_DOMAIN_IDS.forEach((domainId) => {
      domains[domainId].reset();
    });
    denialQueryBucket.reset();
    candidHistoryWriteback.reset();
  }, [
    generalOrderDetails.reset,
    authOnFileDetails.reset,
    clinicalData.reset,
    commentsData.reset,
    coverageDetails.reset,
    workflowConfig.reset,
    reviewsData.reset,
    clinicalConditions.reset,
    authorizationData.reset,
    submissionData.reset,
    siblingOrders.reset,
    denialQueryBucket.reset,
    candidHistoryWriteback.reset,
  ]);

  const abortAll = useCallback(() => {
    fetchInFlightRef.current = null;
    SINGLE_ORDER_DOMAIN_IDS.forEach((domainId) => {
      domains[domainId].abort();
    });
    denialQueryBucket.abort();
    candidHistoryWriteback.abort();
  }, [
    generalOrderDetails.abort,
    authOnFileDetails.abort,
    clinicalData.abort,
    commentsData.abort,
    coverageDetails.abort,
    workflowConfig.abort,
    reviewsData.abort,
    clinicalConditions.abort,
    authorizationData.abort,
    submissionData.abort,
    siblingOrders.abort,
    denialQueryBucket.abort,
    candidHistoryWriteback.abort,
  ]);

  const fetchAll = useCallback(
    async (orderId: string, orgId: string, options?: { force?: boolean }) => {
      const force = options?.force ?? true;
      const key = `${orderId}::${orgId}`;

      if (!force && loadedKeyRef.current === key) return;
      if (fetchInFlightRef.current === key) return;
      fetchInFlightRef.current = key;
      const generation = fetchGenerationRef.current;

      try {
        const { date, time_offset } = getLocalDenialQueryBucketTimeContext();
        const results = await Promise.all([
          ...SINGLE_ORDER_DOMAIN_IDS.map((domainId) =>
            domains[domainId].runFetch(orderId, orgId),
          ),
          denialQueryBucket.runFetch({
            order_id: orderId,
            date,
            time_offset,
            limit: DENIAL_QUERY_BUCKET_LIMIT,
          }),
          candidHistoryWriteback.runFetch(orderId, orgId),
        ]);
        if (
          fetchGenerationRef.current === generation &&
          results.every((result) => result?.success)
        ) {
          loadedKeyRef.current = key;
        }
      } finally {
        if (
          fetchGenerationRef.current === generation &&
          fetchInFlightRef.current === key
        ) {
          fetchInFlightRef.current = null;
        }
      }
    },
    [
      generalOrderDetails.runFetch,
      authOnFileDetails.runFetch,
      clinicalData.runFetch,
      commentsData.runFetch,
      coverageDetails.runFetch,
      workflowConfig.runFetch,
      reviewsData.runFetch,
      clinicalConditions.runFetch,
      authorizationData.runFetch,
      submissionData.runFetch,
      siblingOrders.runFetch,
      denialQueryBucket.runFetch,
      candidHistoryWriteback.runFetch,
    ],
  );

  const loading =
    SINGLE_ORDER_DOMAIN_IDS.some(
      (domainId) =>
        SINGLE_ORDER_DOMAIN_ORCHESTRATION[domainId].includeInCombinedLoading &&
        domains[domainId].loading,
    ) ||
    denialQueryBucket.loading ||
    candidHistoryWriteback.loading;

  return {
    domains,
    denialQueryBucket,
    candidHistoryWriteback,
    fetchAll,
    abortAll,
    resetAll,
    loading,
  };
};
